import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Banner, FormShell } from "@/components/auth/FormShell";
import formStyles from "@/components/auth/AuthForm.module.css";
import { forgotPassword, type AuthError } from "@/lib/auth";

export function ResetSent() {
  const [params] = useSearchParams();
  const email = (params.get("email") ?? "").trim();
  const [error, setError] = useState<AuthError | null>(null);
  const [resentAt, setResentAt] = useState<number | null>(null);
  const [resending, setResending] = useState(false);

  async function onResend() {
    if (!email) return;
    setError(null);
    setResending(true);
    try {
      await forgotPassword(email);
      setResentAt(Date.now());
    } catch (err) {
      setError(err as AuthError);
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthLayout view="reset-sent">
      <FormShell title="Check your email.">
        <div className={formStyles.stack}>
          <div className={formStyles.successHero}>
            <Icon name="check" size={28} strokeWidth={3} />
          </div>
          <p className={formStyles.muted}>
            We sent a 6-digit code to{" "}
            <span className={formStyles.emailEmphasis}>{email || "your email"}</span>.
          </p>
          <p className={formStyles.mutedSmall}>
            Codes expire in 30 minutes. If you don’t see it in 5 minutes, check spam — or
            request a fresh one below.
          </p>
          {error && (
            <Banner variant="danger" icon={<Icon name="info" size={14} />}>
              {error.message}
            </Banner>
          )}
          {resentAt && !error && (
            <Banner variant="success" icon={<Icon name="check" size={14} />}>
              Sent a fresh code — your inbox should refresh in a moment.
            </Banner>
          )}
          <div className={formStyles.actionRow}>
            <Btn
              variant="primary"
              href={`/reset/confirm?email=${encodeURIComponent(email)}`}
              iconRight={<Icon name="arrow-right" size={14} />}
            >
              I have the code
            </Btn>
            <Btn
              variant="secondary"
              iconLeft={<Icon name="refresh" size={12} />}
              onClick={onResend}
              loading={resending}
            >
              Resend code
            </Btn>
          </div>
          <Banner variant="info" icon={<Icon name="info" size={14} />}>
            For your security we don’t reveal whether an account exists for that email.
            Double-check the address if no code arrives.
          </Banner>
          <div className={formStyles.backRow}>
            <Link to="/signin" className={formStyles.backLink}>
              <Icon name="arrow-left" size={12} />
              Back to sign in
            </Link>
          </div>
        </div>
      </FormShell>
    </AuthLayout>
  );
}
